import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailCard, getDetailWellClass } from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import {
  useFsnNetworkIntegration,
  useRefreshFsnNetworkSalesOrderMutation,
} from '@/lib/services/fsn-network/fsn-network.query'
import {
  canSendOrderThroughFsnNetwork,
  parseOptionalFsnMoney,
} from '@/lib/services/fsn-network/fsn-network.utils'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { cn, formatCurrency, getErrorMessage, readProperty, type NumericInput } from '@/lib/utils'
import { AlertTriangle, CircleDollarSign, MapPin, RefreshCw, Send } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import type { CountryCode } from 'libphonenumber-js'
import type { LucideIcon } from 'lucide-react'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import type { OrderFulfillment } from '@/lib/services/sales-orders/sales-orders.types'
import type { FsnNetworkEligibilityOrder } from './fsn-network-eligibility'
import { toast } from 'sonner'
import { getSafeDayjs } from './delivery-share.utils'
import { hasFsnFiller, isRefundedSalesOrder } from './sales-order-presentation'
import {
  FsnNetworkSendDialog,
  getFsnNetworkOrderEligibility,
  type FsnNetworkSendOrder,
} from './FsnNetworkSendDialog'

export { getFsnNetworkOrderEligibility } from './FsnNetworkSendDialog'

export type FsnNetworkFulfillmentOrder = FsnNetworkSendOrder &
  FsnNetworkEligibilityOrder &
  Pick<SalesOrder, 'refund_status'> &
  Partial<
    Pick<
      SalesOrder,
      'fulfillment_status' | 'fulfilled_at' | 'fsn_network_integration' | 'delivery_fee_source'
    >
  > & {
    order_fulfillment?:
      | (Pick<OrderFulfillment, 'type' | 'external_fulfillment_id' | 'metadata'> &
          Partial<Pick<OrderFulfillment, 'status'>>)
      | null
    document?: Pick<NonNullable<SalesOrder['document']>, 'relay_fee'> | null
  }

interface FsnStatusBadge {
  label: string
  variant: BadgeProps['variant']
}

interface FsnFillerDisplay {
  value: string
  detail: string | null
}

type FsnPhoneFormatter = (phone: string, countryCode?: CountryCode) => string

// Saved provider metadata is raw JSON; retain the panel's existing coercion of legacy values.
function providerText(value: unknown): string {
  return String(value)
}

// FsnNetworkOrderService saves money as decimal strings and timestamps/errors as strings.
// Treat unreadable optional metadata as absent, so it cannot abort the order panel.
function readFsnString(fsn: unknown, key: string): string | undefined {
  const value = readProperty(fsn, key)
  return typeof value === 'string' ? value : undefined
}

function readFsnScalar(fsn: unknown, key: string): number | string | undefined {
  const value = readProperty(fsn, key)
  return typeof value === 'string' || typeof value === 'number' ? value : undefined
}

const FSN_FULFILLMENT_TYPE = 'fsn_network'
const FSN_DELIVERY_CONFIRMATION_DELIVERED = 'delivered'

function getFsnStatusBadge(fsn: unknown = {}, isSubmitted = false): FsnStatusBadge {
  const status = readProperty(fsn, 'status')

  switch (status) {
    case 'Complete':
      if (
        providerText(
          readProperty(readProperty(fsn, 'delivery_confirmation'), 'status') ?? ''
        ).toLowerCase() !== FSN_DELIVERY_CONFIRMATION_DELIVERED
      ) {
        return { label: 'Accepted', variant: 'muted' }
      }

      return { label: 'Fulfilled', variant: 'success' }
    case 'Accepted':
      return { label: 'Accepted', variant: 'muted' }
    case 'Processing':
      return isSubmitted
        ? { label: 'Sent', variant: 'muted' }
        : { label: 'Not sent', variant: 'outline' }
    case 'Problem':
      return { label: 'Problem', variant: 'warning' }
    case 'Cancelled':
      return { label: 'Cancelled', variant: 'destructive' }
    default:
      if (isSubmitted) {
        return { label: 'Sent', variant: 'muted' }
      }

      return { label: 'Not sent', variant: 'outline' }
  }
}

function formatPanelMoney(
  amount: number,
  currency: string | null = 'USD',
  isDeduction = false
): string {
  const formatted = formatCurrency(amount, currency || 'USD')

  return isDeduction && amount > 0 ? `-${formatted}` : formatted
}

function MoneyDetail({
  label,
  value,
  currency = 'USD',
  tone = 'default',
  isDeduction = false,
  showWhenEmpty = false,
  emptyText = 'Not returned yet',
}: {
  label: string
  value?: NumericInput
  currency?: string | null
  tone?: 'default' | 'soft'
  isDeduction?: boolean
  showWhenEmpty?: boolean
  emptyText?: string
}): ReactElement | null {
  const amount = parseOptionalFsnMoney(value)

  if (amount === null && !showWhenEmpty) {
    return null
  }

  const formattedAmount = amount === null ? null : formatPanelMoney(amount, currency, isDeduction)
  const hasFormattedAmount = formattedAmount !== null && formattedAmount !== ''

  return (
    <div
      className={cn(
        'min-w-0',
        getDetailWellClass({
          compact: true,
          tone: tone === 'soft' ? 'neutral' : 'periwinkle',
        })
      )}
    >
      <Typography variant="eyebrow" className="block">
        {label}
      </Typography>
      {hasFormattedAmount ? (
        <Typography
          variant="h5"
          className="mt-1 pb-0 tabular-nums break-words"
          title={formattedAmount}
        >
          {formattedAmount}
        </Typography>
      ) : (
        <Typography variant="supporting" className="mt-1 block">
          {emptyText}
        </Typography>
      )}
    </div>
  )
}

function InfoDetail({
  label,
  value,
  detail,
  icon: Icon,
  badge,
}: {
  label: string
  value?: string | null
  detail?: string | null
  icon?: LucideIcon
  badge?: FsnStatusBadge | null
}): ReactElement | null {
  if (!value && !detail) {
    return null
  }

  return (
    <div className={cn('min-w-0', getDetailWellClass({ compact: true, tone: 'mint' }))}>
      <div className="flex min-w-0 items-start gap-2">
        {Icon ? (
          <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : null}
        <div className="min-w-0">
          <Typography variant="eyebrow" className="block">
            {label}
          </Typography>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
            {value ? (
              <Typography variant="h5" className="pb-0 break-words" title={String(value)}>
                {value}
              </Typography>
            ) : null}
            {badge ? (
              <Badge variant={badge.variant ?? 'muted'} size="xs">
                {badge.label}
              </Badge>
            ) : null}
          </div>
          {detail ? (
            <Typography
              variant="supporting"
              className="mt-1 block break-words"
              title={String(detail)}
            >
              {detail}
            </Typography>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function EarningsSummary({
  amount,
  currency = 'USD',
}: {
  amount: number | null
  currency?: string | null
}): ReactElement | null {
  if (amount === null) {
    return null
  }

  const isNegative = amount < 0

  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border px-4 py-3 shadow-none',
        isNegative
          ? 'border-status-warning-border bg-status-warning-background text-status-warning-foreground ring-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground dark:ring-status-warning-border'
          : 'border-status-success-border bg-status-success-background text-status-success-foreground ring-status-success-border dark:bg-status-success-background dark:text-status-success-foreground dark:ring-status-success-border'
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <CircleDollarSign className="mt-0.5 size-5 shrink-0" />
        <div className="min-w-0">
          <Typography variant="eyebrow" tone={isNegative ? 'warning' : 'success'} className="block">
            Estimated earnings
          </Typography>
          <div className="mt-1 text-2xl font-semibold leading-tight tabular-nums sm:text-3xl">
            {formatCurrency(amount, currency || 'USD')}
          </div>
          <Typography
            variant="supporting"
            tone={isNegative ? 'warning' : 'success'}
            className="mt-1 block"
          >
            After filling florist payout and FSN fee
          </Typography>
        </div>
      </div>
    </div>
  )
}

export function getFsnFillerDisplay(
  filler: unknown,
  formatPhone: FsnPhoneFormatter = (value) => value
): FsnFillerDisplay | null {
  if (!filler) {
    return null
  }

  const name = providerText(readProperty(filler, 'name') ?? '').trim()
  const rawPhone = providerText(
    readProperty(filler, 'phone_number') ??
      readProperty(filler, 'phoneNumber') ??
      readProperty(filler, 'local') ??
      readProperty(filler, 'phone') ??
      ''
  ).trim()
  const phone = rawPhone ? formatPhone(rawPhone, 'US') : ''

  if (!name) {
    return {
      value: 'No florist assigned yet',
      detail: null,
    }
  }

  return {
    value: name,
    detail: phone || null,
  }
}

export default function FsnNetworkFulfillmentPanel({
  order,
  isReadOnly = false,
}: {
  order: FsnNetworkFulfillmentOrder
  isReadOnly?: boolean
}): ReactElement | null {
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const { formatPhone } = usePhoneFormatter()
  const orderFulfillment = order?.order_fulfillment ?? null
  const isSubmitted =
    orderFulfillment?.type === FSN_FULFILLMENT_TYPE &&
    Boolean(orderFulfillment?.external_fulfillment_id)
  const isDelivery = order?.fulfillment_type === FulfillmentType.Delivery.Value
  const deliveryServiceabilityStatus = order?.delivery_serviceability_status ?? null
  const isOutsideAreaOrder =
    deliveryServiceabilityStatus === DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
  const canEvaluateForNetworkFulfillment =
    deliveryServiceabilityStatus === null || isOutsideAreaOrder
  const isFulfilledOrder =
    order?.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value ||
    Boolean(order?.fulfilled_at)
  const canPrepareUnsubmittedNetworkFulfillment =
    isDelivery && canEvaluateForNetworkFulfillment && !isFulfilledOrder
  const embeddedIntegration = order?.fsn_network_integration ?? null
  const { data: integration = embeddedIntegration ?? {} } = useFsnNetworkIntegration({
    organizationId: order?.organization_id,
    enabled: Boolean(
      order?.organization_id && (isSubmitted || canPrepareUnsubmittedNetworkFulfillment)
    ),
    ...(embeddedIntegration ? { placeholderData: embeddedIntegration } : {}),
  })
  const refreshMutation = useRefreshFsnNetworkSalesOrderMutation()
  const fsn = readProperty(orderFulfillment?.metadata, 'fsn') ?? {}
  const rawCurrency = readFsnString(fsn, 'currency')
  const currency = rawCurrency && /^[a-z]{3}$/i.test(rawCurrency) ? rawCurrency : undefined
  const orderSplit = readFsnScalar(fsn, 'order_split')
  const lastSyncedAt = getSafeDayjs(readFsnString(fsn, 'last_synced_at'))
  const lastError = readFsnString(fsn, 'last_error')
  const status = getFsnStatusBadge(fsn, isSubmitted)
  const eligibility = getFsnNetworkOrderEligibility(order)
  // Current settings only decide whether a new order can be sent. Once an order
  // has an FSN fulfillment, the order's saved fulfillment state keeps this panel visible.
  const canStartNetworkFulfillment =
    canPrepareUnsubmittedNetworkFulfillment && canSendOrderThroughFsnNetwork(order, integration)
  const hasReturnedFiller = hasFsnFiller(readProperty(fsn, 'filler'))
  const requestedFloristDisplay = getFsnFillerDisplay(
    readProperty(fsn, 'requested_florist'),
    formatPhone
  )
  const fillerDisplay = getFsnFillerDisplay(
    hasReturnedFiller ? readProperty(fsn, 'filler') : readProperty(fsn, 'requested_florist'),
    formatPhone
  ) ?? {
    value: 'No florist assigned yet',
    detail: null,
  }
  const fillerBadge: FsnStatusBadge | null =
    requestedFloristDisplay && !hasReturnedFiller ? { label: 'Requested', variant: 'info' } : null
  const orderValuePreview = order?.fsn_network_order_preview ?? {}
  const estimatedEarnings = parseOptionalFsnMoney(orderValuePreview.estimated_earnings)
  const fillingFloristPayout = parseOptionalFsnMoney(orderValuePreview.filling_florist_payout)
  const previewCurrency = order?.currency_type ?? 'USD'
  const previewOrderValue = parseOptionalFsnMoney(orderValuePreview.order_value)
  const formattedOrderValue =
    previewOrderValue !== null ? formatCurrency(previewOrderValue, previewCurrency) : null
  const isComplete = readProperty(fsn, 'status') === 'Complete'
  const isDeliveryConfirmed =
    isComplete &&
    providerText(
      readProperty(readProperty(fsn, 'delivery_confirmation'), 'status') ?? ''
    ).toLowerCase() === FSN_DELIVERY_CONFIRMATION_DELIVERED
  const isProblem = readProperty(fsn, 'status') === 'Problem'
  const isCancelled = readProperty(fsn, 'status') === 'Cancelled'
  const isRefunded = isRefundedSalesOrder(order)
  if (!isDelivery && !isSubmitted) {
    return null
  }

  if (!isSubmitted && !canStartNetworkFulfillment) {
    return null
  }

  async function handleRefresh() {
    try {
      await refreshMutation.mutateAsync({ salesOrderId: order.id })
      toast.success('Flower Shop Network status refreshed')
    } catch (error) {
      toast.error('Could not refresh Flower Shop Network status', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <>
      <DetailCard
        accent={isDeliveryConfirmed ? 'success' : 'periwinkle'}
        contentClassName="space-y-4 p-4 sm:p-5"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <FsnNetworkIcon className="mt-0.5 size-5" />
            <div className="min-w-0">
              <Typography variant="eyebrow" className="block">
                Out-of-area delivery
              </Typography>
              <Typography variant="h4" className="mt-1 pb-0">
                {isDeliveryConfirmed
                  ? 'Fulfilled by Flower Shop Network'
                  : isSubmitted
                    ? isComplete
                      ? 'Accepted by Flower Shop Network'
                      : 'Sent to Flower Shop Network'
                    : 'Send to Flower Shop Network'}
              </Typography>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
            <Badge variant={status.variant} size="md">
              {status.label}
            </Badge>
            {isSubmitted ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isReadOnly || refreshMutation.isPending}
                onClick={handleRefresh}
              >
                <RefreshCw className="size-4" />
                {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Status'}
              </Button>
            ) : null}
          </div>
        </div>

        {isSubmitted ? (
          <div className="space-y-3">
            {!isCancelled ? (
              <>
                <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(240px,1fr)]">
                  <EarningsSummary amount={estimatedEarnings} currency={currency} />
                  <InfoDetail
                    label="Filling location"
                    value={fillerDisplay.value}
                    detail={fillerDisplay.detail}
                    icon={MapPin}
                    badge={fillerBadge}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <MoneyDetail
                    label="Order value"
                    value={readFsnScalar(fsn, 'order_value')}
                    currency={currency}
                  />
                  <MoneyDetail
                    label="Relay fee retained"
                    value={order.document?.relay_fee}
                    currency={currency}
                    tone="soft"
                    showWhenEmpty
                    emptyText="$0.00"
                  />
                  <MoneyDetail
                    label="Filling florist payout"
                    value={fillingFloristPayout}
                    currency={currency}
                    tone="soft"
                    isDeduction
                    showWhenEmpty
                    emptyText="Not returned by FSN"
                  />
                  <MoneyDetail
                    label="FSN fee"
                    value={readFsnScalar(fsn, 'network_fee')}
                    currency={currency}
                    tone="soft"
                    isDeduction
                    showWhenEmpty
                    emptyText="Not returned by FSN"
                  />
                </div>
              </>
            ) : null}

            {orderFulfillment.external_fulfillment_id || orderSplit || lastSyncedAt ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {orderFulfillment.external_fulfillment_id ? (
                  <Typography variant="supporting" className="block">
                    Network order #{orderFulfillment.external_fulfillment_id}
                  </Typography>
                ) : null}
                {orderSplit ? (
                  <Typography variant="supporting" className="block">
                    Order split: {orderSplit}%
                  </Typography>
                ) : null}
                {lastSyncedAt ? (
                  <Typography variant="supporting" className="block">
                    Last checked {lastSyncedAt.format('MMM D, YYYY h:mm A')}
                  </Typography>
                ) : null}
              </div>
            ) : null}

            {isProblem ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Flower Shop Network status: Problem</AlertTitle>
                <AlertDescription>
                  This order still needs attention in Flower Shop Network. Call FSN F2F support at
                  (877) 376-7363.
                </AlertDescription>
              </Alert>
            ) : null}

            {isCancelled ? (
              <Alert variant={isRefunded ? 'warning' : 'destructive'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Cancelled in Flower Shop Network</AlertTitle>
                <AlertDescription>
                  {isRefunded
                    ? 'This order was cancelled in Flower Shop Network.'
                    : 'Flower Shop Network cancelled this order. That does not refund your customer here. Refund the customer from this order if needed.'}
                </AlertDescription>
              </Alert>
            ) : null}

            {lastError ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Last update failed</AlertTitle>
                <AlertDescription>{lastError}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : canStartNetworkFulfillment ? (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <Typography variant="supporting" className="block max-w-2xl">
                  Use this when you want Flower Shop Network to fill the order.
                </Typography>
                {formattedOrderValue ? (
                  <Typography variant="label" tone="muted" className="mt-1 block">
                    FSN order value:{' '}
                    <span className="font-semibold text-foreground">{formattedOrderValue}</span>
                  </Typography>
                ) : null}
              </div>
              <Button
                type="button"
                className="w-full md:w-auto"
                disabled={isReadOnly || !eligibility.isEligible}
                onClick={() => setSendDialogOpen(true)}
              >
                <Send className="size-4" />
                Send to Flower Shop Network
              </Button>
            </div>

            {!eligibility.isEligible ? (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Finish order details first</AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {eligibility.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : null}
      </DetailCard>

      <FsnNetworkSendDialog order={order} open={sendDialogOpen} onOpenChange={setSendDialogOpen} />
    </>
  )
}
